/**
 * TermReleaseBanner.js - Banner ceria di dashboard ibu bapa bila Laporan Penggal dirilis.
 * Dipapar bermula tarikh rilis selama 7 hari, atau sehingga ibu bapa tekan
 * "Lihat Laporan" / tutup (disimpan dalam localStorage peranti).
 */
import { useState, useEffect, useMemo, useRef } from "react";
import confetti from "canvas-confetti";
import { PartyPopper, X, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const SHOW_DAYS = 7;
const KEY = (item) => `dj_term_release_banner_${item.child_id}_${item.latest?.term_key || ""}`;

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const rel = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - rel) / 86400000);
};

export const getActiveReleases = (items = []) =>
  items.filter((it) => {
    const n = daysSince(it.release_date);
    if (n === null || n < 0 || n >= SHOW_DAYS) return false;
    try { return !localStorage.getItem(KEY(it)); } catch (e) { return true; }
  });

export default function TermReleaseBanner({ items = [], onView }) {
  const [dismissed, setDismissed] = useState(false);
  const active = useMemo(() => getActiveReleases(items), [items]);
  const fired = useRef(false);

  useEffect(() => {
    if (!active.length || fired.current) return;
    fired.current = true;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) return;
    const t = setTimeout(() => {
      confetti({ particleCount: 90, spread: 75, origin: { y: 0.25 }, colors: ["#a855f7", "#ec4899", "#f59e0b", "#22c55e", "#3b82f6"] });
      setTimeout(() => confetti({ particleCount: 50, angle: 60, spread: 60, origin: { x: 0, y: 0.4 } }), 250);
      setTimeout(() => confetti({ particleCount: 50, angle: 120, spread: 60, origin: { x: 1, y: 0.4 } }), 400);
    }, 600);
    return () => clearTimeout(t);
  }, [active.length]);

  if (dismissed || !active.length) return null;

  const remember = () => {
    active.forEach((it) => { try { localStorage.setItem(KEY(it), new Date().toISOString()); } catch (e) { /* abaikan */ } });
    setDismissed(true);
  };

  const names = active.map((it) => it.child_name).filter(Boolean);
  const termLabel = active[0]?.latest?.term_label || "Laporan Penggal";

  return (
    <div
      className="relative overflow-hidden rounded-3xl mb-8 p-5 sm:p-6 border-2 border-white shadow-xl bg-gradient-to-r from-amber-100 via-pink-100 to-purple-100 animate-in fade-in slide-in-from-top-4 duration-700"
      data-testid="term-release-banner"
    >
      <Sparkles className="absolute -top-2 right-16 w-16 h-16 text-yellow-300/60 rotate-12 pointer-events-none" />
      <Sparkles className="absolute bottom-0 left-1/2 w-10 h-10 text-pink-300/50 pointer-events-none" />
      <button
        onClick={remember}
        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/70 hover:bg-white text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors"
        aria-label="Tutup"
        data-testid="term-release-banner-close"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 pr-8">
        <div className="w-16 h-16 shrink-0 rounded-2xl bg-white shadow-md flex items-center justify-center animate-bounce [animation-duration:2s]">
          <PartyPopper className="w-9 h-9 text-pink-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-purple-600">Yeay! Laporan sudah dirilis</p>
          <h3 className="text-xl sm:text-2xl font-black text-gray-800 leading-tight mt-0.5">
            {termLabel} {names.length === 1 ? `untuk ${names[0]}` : "anak anda"} kini tersedia!
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {names.length > 1 ? `${names.join(", ")} — ` : ""}Lihat komen guru dan kemajuan perkembangan anak sepanjang penggal ini.
          </p>
        </div>
        <Button
          onClick={() => { remember(); onView?.(active[0]); }}
          className="shrink-0 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 h-12 shadow-lg"
          data-testid="term-release-banner-view"
        >
          Lihat Laporan <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
